import {
  AnyID,
  Getter,
  Item,
  ItemGroup,
  LinkedItem,
  Partition,
  SceneObjects,
  Setter,
  Store,
  Types,
  Walls,
} from '@types'
import toQuaternion from '@utils/conversion/toQuaternion'
import { toVector3 } from '@utils/conversion/toVector3'
import decomposeMatrix from '@utils/decomposeMatrix'
import isWall from '@utils/sceneObjects/assertions/isWall'
import { purgeSceneGlbForCatalog } from '@utils/sceneObjects/purgeSceneGlbForCatalog'
import { removeLinkedHierarchyFromStore } from '@utils/sceneObjects/removeLinkedHierarchyFromStore'
import { getWallCOBB } from '@utils/sceneObjects/walls/getWallCOBB'
import { Camera, Euler, Matrix3, Matrix4, Vector2, Vector3 } from 'three'
import { OBB } from 'three/examples/jsm/math/OBB'
export type SceneActions = ReturnType<typeof createSceneActions>

export const createSceneActions = (set: Setter, get: Getter) => ({
  select: (id: AnyID, isCtrlPressed: boolean) => {
    set((store) => {
      const selectedItems = store.layers[store.currentLayer].selected as Set<string>
      const newSelectedItems = new Set(selectedItems)

      // Si no hay control presionado, limpiar la selección en ciertos casos
      if (
        (!isCtrlPressed && selectedItems.size < 2) ||
        (!isCtrlPressed && !selectedItems.has(id))
      ) {
        newSelectedItems.clear()
      }

      // Si hay control presionado y el item ya está seleccionado, quitarlo
      if (isCtrlPressed && selectedItems.has(id)) {
        newSelectedItems.delete(id)
        store.currentObjectID = ''
      } else {
        // Agregar el item a la selección
        newSelectedItems.add(id)
        store.currentObjectID = id
      }

      // Asignar el nuevo Set para triggerar reactividad
      store.layers[store.currentLayer].selected = newSelectedItems
    })
  },
  unselect: (id: AnyID) => {
    set((store) => {
      const selectedItems = store.layers[store.currentLayer].selected as Set<string>
      const newSelectedItems = new Set(selectedItems)
      newSelectedItems.delete(id)
      store.layers[store.currentLayer].selected = newSelectedItems
      store.currentObjectID === id ? (store.currentObjectID = '') : ''
    })
  },

  unselectAll: () => {
    set((store) => {
      store.layers[store.currentLayer].selected = new Set()
      store.currentObjectID = ''
    })
  },
  // TODO: esto debería no existir: Es parte de multiselección
  deleteSelectedObjects: () => {
    set((store: Store) => {
      const selected = store.layers[store.currentLayer].selected as Set<string>
      if (selected) {
        selected.forEach((el: string) => {
          if (!el) return

          const currItem = store.items.get(el) as SceneObjects
          if (!currItem) return

          // Si es un itemGroup, borrar el grupo y todos sus hijos
          if (currItem.type === 'itemGroup') {
            const group = currItem as ItemGroup
            const childrenIds = Array.from(group.children) as AnyID[]

            // Borrar cada hijo del grupo
            childrenIds.forEach((childId) => {
              const child = store.items.get(childId)

              if (child && child.type === 'linkedItem') {
                removeLinkedHierarchyFromStore(store, childId)
              }

              // Borrar el hijo
              if (child) {
                const childCatalogID = child.catalogID
                const childCodigo = (child as { codigo?: string }).codigo
                store.price -= child.price || 0
                store.items.delete(childId)
                store.sceneBoundingBoxes.delete(childId)
                store.collisionableItems.delete(childId)
                store.layers[store.currentLayer].items.delete(childId)
                purgeSceneGlbForCatalog(store, childCatalogID, childCodigo)
              }
            })

            // Borrar el grupo mismo
            store.price -= group.price || 0
            store.items.delete(el)
            store.sceneBoundingBoxes.delete(el)
            store.collisionableItems.delete(el)
            store.layers[store.currentLayer].items.delete(el)
            return
          }

          if (currItem.type === 'linkedItem') {
            removeLinkedHierarchyFromStore(store, el)
          }

          if (currItem.snappedWall) {
            const wall = store.items.get(currItem.snappedWall) as Walls
            if (wall) {
              if (currItem.tipoNormalizado === 'MAL') {
                wall.highItems.delete(el)
              }
              if (currItem.tipoNormalizado === 'MBJ' || currItem.tipoNormalizado === 'COL') {
                wall.lowItems.delete(el)
              }
            }
          }

          store.price -= currItem.price || 0

          const deletedCatalogID = currItem.catalogID
          const deletedCodigo = (currItem as { codigo?: string }).codigo
          store.items.delete(el)
          store.sceneBoundingBoxes.delete(el)
          store.collisionableItems.delete(el)
          store.sceneBoundingBoxes.delete(getWallCOBB(el))
          store.collisionableItems.delete(getWallCOBB(el))
          store.layers[store.currentLayer].items.delete(el)
          purgeSceneGlbForCatalog(store, deletedCatalogID, deletedCodigo)
        })
        selected.clear()
      }
      store.currentObjectID = ''
    })
  },

  setCurrentObjectID: (id: AnyID | '') => {
    set((store) => {
      if (!id && id !== '') return

      store.currentObjectID = id
    })
  },
  //TO-DO:esto no se esta usando no hace falta pa nada
  addBoundingBox: (id: string, boundingObject: { type: Types; boundingBox: OBB }) => {
    set((store) => {
      store.sceneBoundingBoxes.set(id, boundingObject)
    })
  },
  setDeltas: (
    deltas: Map<
      string,
      Array<{ targetId: string; deltaX: number; deltaY: number; deltaZ: number }>
    >,
  ) => {
    set((store) => {
      deltas.forEach((value, key) => {
        store.layers[store.currentLayer].deltas.set(key, value)
      })
    })
  },
  notSameItems: () => {
    const store = get()
    const selectedItems = new Set(
      Array.from(store.layers[store.currentLayer].selected).filter((id) => store.items.has(id)),
    )

    // Mostrar sidebar de multiselección cuando hay 2 o más items seleccionados
    // (ya sean iguales o diferentes)
    if (selectedItems.size < 2) return false

    // Verificar que todos los items seleccionados sean items o linkedItems válidos
    for (const el of selectedItems) {
      const currItem = store.items.get(el) as SceneObjects

      if (!currItem) {
        console.warn(`⚠️ El objeto con ID ${el} no se encontró en store.items`)
        continue
      }

      // Validar que sea un tipo de objeto editable (items, linkedItems, partitions)
      if (!['items', 'linkedItem', 'partition'].includes(currItem.type)) {
        return false
      }
    }

    return true
  },

  updateSelectedObjectsPosition: (currentObjectId: AnyID, matrix: Matrix4) => {
    set((store) => {
      const selectedItems = store.layers[store.currentLayer].selected as Set<AnyID>
      const items = store.items
      const deltas = store.layers[store.currentLayer].deltas

      if (selectedItems.size < 2) return

      // Obtener la posición del elemento que se está arrastrando
      const currentItem = items.get(currentObjectId) as Item | Partition | LinkedItem
      if (!currentItem) return

      const { position: currentPosition } = decomposeMatrix(matrix)

      // Aplicar restricción al currentPosition.y también para evitar que se propague valores negativos
      const mode = store.mode
      let restrictedCurrentY = currentPosition.y
      if (mode === 'MODE_3D_VIEW' || mode === 'MODE_FORCE_ALZADOS' || mode === 'MODE_ALZADOS') {
        // Restringir mínimo a 0 siempre
        restrictedCurrentY = Math.max(0, restrictedCurrentY)

        // Si el item actual tiene altura, aplicar restricción máxima
        if ('height' in currentItem && currentItem.height) {
          // Obtener altura máxima de la pared
          let maxWallHeight: number | null = null

          // Si el item está pegado a una pared, usar esa pared
          if ('snappedWall' in currentItem && currentItem.snappedWall) {
            const wall = items.get(currentItem.snappedWall) as Walls
            if (isWall(wall)) {
              maxWallHeight = wall.height
            }
          } else {
            // Si no está pegado, obtener la altura máxima de todas las paredes
            let maxHeight = 0
            items.forEach((sceneObject) => {
              if (isWall(sceneObject) && sceneObject.height > maxHeight) {
                maxHeight = sceneObject.height
              }
            })
            maxWallHeight = maxHeight > 0 ? maxHeight : null
          }

          // Restringir máximo considerando la altura del mueble
          if (maxWallHeight !== null) {
            const maxYAllowed = maxWallHeight - currentItem.height
            restrictedCurrentY = Math.min(restrictedCurrentY, maxYAllowed)
          }
        }
      }

      // Actualizar las posiciones de todos los elementos seleccionados
      selectedItems.forEach((id) => {
        if (id === currentObjectId) return // Saltar el elemento que se está arrastrando

        const item = items.get(id) as Item | Partition | LinkedItem
        if (!item) return

        const itemDeltas = deltas?.get(currentObjectId)
        if (!itemDeltas) return

        itemDeltas.forEach((delta) => {
          if (delta.targetId === id) {
            // Usar el currentPosition.y restringido como base
            let newY = restrictedCurrentY + delta.deltaY

            // Aplicar restricción de altura en modo 3D o alzados
            if (
              mode === 'MODE_3D_VIEW' ||
              mode === 'MODE_FORCE_ALZADOS' ||
              mode === 'MODE_ALZADOS'
            ) {
              // Restringir mínimo a 0 siempre
              newY = Math.max(0, newY)

              // Si el item tiene altura, aplicar restricción máxima
              if ('height' in item && item.height) {
                // Obtener altura máxima de la pared
                let maxWallHeight: number | null = null

                // Si el item está pegado a una pared, usar esa pared
                if ('snappedWall' in item && item.snappedWall) {
                  const wall = items.get(item.snappedWall) as Walls
                  if (isWall(wall)) {
                    maxWallHeight = wall.height
                  }
                } else {
                  // Si no está pegado, obtener la altura máxima de todas las paredes
                  let maxHeight = 0
                  items.forEach((sceneObject) => {
                    if (isWall(sceneObject) && sceneObject.height > maxHeight) {
                      maxHeight = sceneObject.height
                    }
                  })
                  maxWallHeight = maxHeight > 0 ? maxHeight : null
                }

                // Restringir máximo considerando la altura del mueble
                if (maxWallHeight !== null) {
                  const maxYAllowed = maxWallHeight - item.height
                  newY = Math.min(newY, maxYAllowed)
                }
              }
            }

            const newPosition = toVector3([
              currentPosition.x + delta.deltaX,
              newY,
              currentPosition.z + delta.deltaZ,
            ])

            const { rotation: originalQuaternion } = decomposeMatrix(item.matrix)
            const newMatrix = new Matrix4()
              .makeRotationFromQuaternion(originalQuaternion)
              .setPosition(newPosition)

            // CORRECCIÓN FINAL: asegurar que newY nunca sea negativo (última línea de defensa)
            const finalY = Math.max(0, newY)

            // Actualizar el item directamente
            item.matrix = newMatrix
            item.x = newPosition.x
            item.y = finalY
            item.z = newPosition.z

            // Si se corrigió el Y, actualizar la matriz también
            if (finalY !== newY) {
              newMatrix.setPosition(newPosition.x, finalY, newPosition.z)
              item.matrix = newMatrix
            }

            // Actualizar en el store
            store.items.set(id, item)

            // Actualizar también el boundingBox para que se refleje correctamente
            const itemCenter = toVector3([item.width / 2, item.height / 2, item.thickness / 2])
            const centerPosition = toVector3()
            newMatrix.decompose(centerPosition, originalQuaternion, toVector3())
            itemCenter.applyQuaternion(originalQuaternion).add(centerPosition)

            const rotationMatrix4 = new Matrix4().makeRotationFromQuaternion(originalQuaternion)
            const rotationMatrix = new Matrix3().setFromMatrix4(rotationMatrix4)

            store.sceneBoundingBoxes.set(id, {
              boundingBox: new OBB(
                itemCenter,
                toVector3([item.width / 2, item.height / 2, item.thickness / 2]),
                rotationMatrix,
              ),
              type: 'items',
            })
          }
        })
      })
    })
  },
  updateSelectedObjectsTraslation: (currentObjectId: AnyID, matrix: Matrix4, prevRot: number) => {
    set((store) => {
      const selectedItems = store.layers[store.currentLayer].selected as Set<AnyID>
      const items = store.items
      const deltas = store.layers[store.currentLayer].deltas

      if (selectedItems.size < 2) return

      // Obtener la matriz del elemento que se está rotando
      const currentItem = items.get(currentObjectId) as Item | Partition | LinkedItem
      if (!currentItem) return

      const { position: currentPosition, rotation: currentRotation } = decomposeMatrix(matrix)

      // Convertir currentRotation a grados
      const currentRotEuler = new Euler().setFromQuaternion(currentRotation, 'YXZ')
      const currentRotDeg = currentRotEuler.y * (180 / Math.PI)

      // Calcular la diferencia de rotación en grados (invirtiendo la dirección)
      const rotationDifferenceDeg = prevRot + currentRotDeg

      // Convertir la diferencia de rotación a radianes
      const rotationDifferenceRad = rotationDifferenceDeg * (Math.PI / 180)

      // Crear una matriz de rotación a partir de la diferencia de rotación en radianes
      const rotationMatrix = new Matrix4().makeRotationY(rotationDifferenceRad)

      // Actualizar las posiciones y rotaciones de todos los elementos seleccionados
      selectedItems.forEach((id) => {
        if (id === currentObjectId) return // Saltar el elemento que se está rotando

        const item = items.get(id) as Item | Partition | LinkedItem
        if (!item) return

        // Obtener los deltas almacenados en el store
        const itemDeltas = deltas.get(currentObjectId)
        if (!itemDeltas) return

        // Encontrar el delta correspondiente al elemento actual
        const delta = itemDeltas.find((d) => d.targetId === id)
        if (!delta) return

        // Crear un vector delta a partir de los valores del delta
        const deltaVector = toVector3([delta.deltaX, 0, delta.deltaZ])

        // Aplicar la rotación al delta (solo en los ejes X y Z)
        const rotatedDelta = deltaVector.applyMatrix4(rotationMatrix)

        // Calcular la nueva posición del punto
        const newPosition = toVector3([
          currentPosition.x + rotatedDelta.x,
          currentPosition.y + delta.deltaY, // Conservar la altura original
          currentPosition.z + rotatedDelta.z,
        ])

        // Descomponer la matriz del item para obtener la rotación actual
        const { rotation: originalQuaternion } = decomposeMatrix(item.matrix)

        // Convertir la rotación actual a Euler y luego a grados
        const originalRotEuler = new Euler().setFromQuaternion(originalQuaternion, 'YXZ')
        const originalRotDeg = originalRotEuler.y * (180 / Math.PI)

        // Calcular la nueva rotación en grados (invirtiendo la dirección)
        const newRotDeg = originalRotDeg + rotationDifferenceDeg

        // Convertir la nueva rotación a radianes y luego a un cuaternión
        const newRotRad = newRotDeg * (Math.PI / 180)
        const newQuaternion = toQuaternion().setFromAxisAngle(toVector3([0, 1, 0]), newRotRad)

        // Crear una nueva matriz con la nueva rotación y la posición original
        const newMatrix = new Matrix4()
          .makeRotationFromQuaternion(newQuaternion)
          .setPosition(newPosition)

        const updatedItem = {
          ...item,
          matrix: newMatrix,
          x: newPosition.x,
          y: newPosition.y,
          z: newPosition.z,
        }

        store.items.set(id, updatedItem)
      })
    })
  },
  updateSelectedObjectsPosition2D: (currentObjectId: AnyID, matrix: Matrix4) => {
    set((store) => {
      const selectedItems = store.layers[store.currentLayer].selected as Set<AnyID>
      const items = store.items
      const deltas = store.layers[store.currentLayer].deltas

      if (selectedItems.size < 2) return

      // Obtener la posición del elemento que se está arrastrando
      const currentItem = items.get(currentObjectId) as Item | Partition | LinkedItem
      if (!currentItem) return

      const { position: currentPosition } = decomposeMatrix(matrix)

      // Actualizar las posiciones de todos los elementos seleccionados
      selectedItems.forEach((id) => {
        if (id === currentObjectId) return // Saltar el elemento que se está arrastrando

        const item = items.get(id) as Item | Partition | LinkedItem
        if (!item) return

        // Obtener posición original del item (extraer de la matriz si x/z/y son undefined)
        let originalX = item.x
        let originalY = item.y
        let originalZ = item.z

        if (originalX === undefined || originalY === undefined || originalZ === undefined) {
          const { position: originalPosition } = decomposeMatrix(item.matrix)
          originalX = originalPosition.x
          originalY = originalPosition.y
          originalZ = originalPosition.z
        }

        const itemDeltas = deltas.get(currentObjectId)
        if (!itemDeltas) return

        itemDeltas.forEach((delta) => {
          if (delta.targetId === id) {
            // Calcular la nueva posición manteniendo las posiciones relativas
            const newPosition = toVector3([
              currentPosition.x + delta.deltaX,
              originalY, // Mantener la coordenada Y original en 2D
              currentPosition.z + delta.deltaZ,
            ])

            // Mantener la rotación original del item
            const { rotation: originalQuaternion } = decomposeMatrix(item.matrix)
            const newMatrix = new Matrix4()
              .makeRotationFromQuaternion(originalQuaternion)
              .setPosition(newPosition)

            // Actualizar el item
            const updatedItem = {
              ...item,
              matrix: newMatrix,
              x: newPosition.x,
              y: originalY,
              z: newPosition.z,
            }

            store.items.set(id, updatedItem)

            // Actualizar también el boundingBox
            const itemCenter = toVector3([item.width / 2, item.height / 2, item.thickness / 2])
            const centerPosition = toVector3()
            newMatrix.decompose(centerPosition, originalQuaternion, toVector3())
            itemCenter.applyQuaternion(originalQuaternion).add(centerPosition)

            const rotationMatrix4 = new Matrix4().makeRotationFromQuaternion(originalQuaternion)
            const rotationMatrix = new Matrix3().setFromMatrix4(rotationMatrix4)

            store.sceneBoundingBoxes.set(id, {
              boundingBox: new OBB(
                itemCenter,
                toVector3([item.width / 2, item.height / 2, item.thickness / 2]),
                rotationMatrix,
              ),
              type: 'items',
            })
          }
        })
      })
    })
  },
  updateSelectedObjectsTraslation2D: (currentObjectId: AnyID, matrix: Matrix4, prevRot: number) => {
    set((store) => {
      const selectedItems = store.layers[store.currentLayer].selected as Set<AnyID>
      const items = store.items
      const deltas = store.layers[store.currentLayer].deltas

      if (selectedItems.size < 2) return

      // Obtener la matriz del elemento que se está rotando
      const currentItem = items.get(currentObjectId) as Item | Partition | LinkedItem
      if (!currentItem) return

      const { position: currentPosition, rotation: currentRotation } = decomposeMatrix(matrix)

      // Convertir currentRotation a grados
      const currentRotEuler = new Euler().setFromQuaternion(currentRotation, 'YXZ')
      const currentRotDeg = currentRotEuler.y * (180 / Math.PI)

      // Calcular la diferencia de rotación en grados (invirtiendo la dirección)
      const rotationDifferenceDeg = prevRot + currentRotDeg

      // Convertir la diferencia de rotación a radianes
      const rotationDifferenceRad = rotationDifferenceDeg * (Math.PI / 180)

      // Crear una matriz de rotación a partir de la diferencia de rotación en radianes
      const rotationMatrix = new Matrix4().makeRotationY(rotationDifferenceRad)

      // Actualizar las posiciones y rotaciones de todos los elementos seleccionados
      selectedItems.forEach((id) => {
        if (id === currentObjectId) return // Saltar el elemento que se está rotando

        const item = items.get(id) as Item | Partition | LinkedItem
        if (!item) return

        // Obtener los deltas almacenados en el store
        const itemDeltas = deltas.get(currentObjectId)
        if (!itemDeltas) return

        // Encontrar el delta correspondiente al elemento actual
        const delta = itemDeltas.find((d) => d.targetId === id)
        if (!delta) return

        // Crear un vector delta a partir de los valores del delta
        const deltaVector = toVector3([delta.deltaX, delta.deltaY, delta.deltaZ])

        // Aplicar la rotación al delta (solo en los ejes X y Z)
        const rotatedDelta = deltaVector.applyMatrix4(rotationMatrix)

        // Calcular la nueva posición del punto
        const newPosition = toVector3([
          currentPosition.x + rotatedDelta.x,
          item.y, // Mantener la coordenada Y sin cambios
          currentPosition.z + rotatedDelta.z,
        ])

        // Descomponer la matriz del item para obtener la rotación actual
        const { rotation: originalQuaternion } = decomposeMatrix(item.matrix)

        // Convertir la rotación actual a Euler y luego a grados
        const originalRotEuler = new Euler().setFromQuaternion(originalQuaternion, 'YXZ')
        const originalRotDeg = originalRotEuler.y * (180 / Math.PI)

        // Calcular la nueva rotación en grados (invirtiendo la dirección)
        const newRotDeg = originalRotDeg + rotationDifferenceDeg

        // Convertir la nueva rotación a radianes y luego a un cuaternión
        const newRotRad = newRotDeg * (Math.PI / 180)
        const newQuaternion = toQuaternion().setFromAxisAngle(toVector3([0, 1, 0]), newRotRad)

        // Crear una nueva matriz con la nueva rotación y la posición original
        const newMatrix = new Matrix4()
          .makeRotationFromQuaternion(newQuaternion)
          .setPosition(newPosition.x, item.y, newPosition.z)

        const updatedItem = {
          ...item,
          matrix: newMatrix,
          x: newPosition.x,
          y: item.y, // Mantener la coordenada Y sin cambios
          z: newPosition.z,
        }

        store.items.set(id, updatedItem)
      })
    })
  },
  projectItemsOnWall: (itemID: AnyID, wallID: AnyID, matrix: Matrix4, prevRot: number) => {
    set((store) => {
      const items = store.items
      const selectedItems = store.layers[store.currentLayer].selected as Set<string>
      const deltas = store.layers[store.currentLayer].deltas as Map<
        string,
        { deltaX: number; deltaY: number; deltaZ: number }[]
      >
      const item = items.get(itemID) as Item
      const wall = items.get(wallID) as Walls

      if (!item || !wall) return

      // Proyectar el item principal en la pared
      const itemMorphs = item.morphs.find((el) => el.name === 'Ancho')
      const itemWidth = itemMorphs?._default ?? itemMorphs?.default ?? 0

      const { rotation, width } = wall

      const itemPosition = toVector3()
      matrix.decompose(itemPosition, toQuaternion(), toVector3())
      const end = toVector3(wall.end)
      const start = toVector3(wall.start)
      const wallDirection = end.clone().sub(start)
      const itemToStart = itemPosition.clone().sub(start)
      const projectedVector = itemToStart.projectOnVector(wallDirection.clone().normalize())

      if (Math.abs(projectedVector.x) === 0) {
        projectedVector.x = 0
      }
      if (Math.abs(projectedVector.z) === 0) {
        projectedVector.z = 0
      }
      if (Math.abs(projectedVector.x) > width - itemWidth) {
        if (wallDirection.x < 0) {
          projectedVector.x = itemWidth - width
        } else {
          projectedVector.x = -itemWidth + width
        }
      }
      if (Math.abs(projectedVector.x) < 0) {
        projectedVector.x = 0
      }
      if (Math.abs(projectedVector.z) > width - itemWidth) {
        if (wallDirection.z < 0) {
          projectedVector.z = itemWidth - width
        } else {
          projectedVector.z = -itemWidth + width
        }
      }
      if (Math.abs(projectedVector.z) < 0) {
        projectedVector.z = -itemWidth
      }

      let length = projectedVector.length()
      const wallLength = wallDirection.length()
      let alongWall = projectedVector.length()

      if (projectedVector.dot(wallDirection.normalize()) < 0) {
        alongWall = 0
      } else if (alongWall > wallLength) {
        alongWall = wallLength - itemWidth
      }

      const adjustedPosition = start
        .clone()
        .add(wallDirection.normalize().multiplyScalar(alongWall))

      if (length < 0) {
        length = 0
      }
      if (length > width || length + item.width > width) {
        length = width
      }

      if (!item) return

      // Aplicar restricción de altura en modo 3D o alzados
      let restrictedY = itemPosition.y
      const mode = store.mode
      if (mode === 'MODE_3D_VIEW' || mode === 'MODE_FORCE_ALZADOS' || mode === 'MODE_ALZADOS') {
        // Restringir mínimo a 0 siempre
        restrictedY = Math.max(0, restrictedY)

        // Si el item tiene altura, aplicar restricción máxima
        if ('height' in item && item.height) {
          // Usar la altura de la pared a la que está pegado
          const maxWallHeight = wall.height

          // Restringir máximo considerando la altura del mueble
          const maxYAllowed = maxWallHeight - item.height
          restrictedY = Math.min(restrictedY, maxYAllowed)
        }
      }

      item.matrix.makeRotationFromEuler(new Euler(0, -rotation, 0, 'XYZ'))

      if (!item.visible) item.visible = true
      item.matrix.setPosition(adjustedPosition.x, restrictedY, adjustedPosition.z)
      item.x = adjustedPosition.x
      item.z = adjustedPosition.z
      item.y = restrictedY
      // ItemGroups no deben tener snappedWall ni agregarse a las paredes
      if (item.type !== 'itemGroup') {
        item.snappedWall = wallID
      }
      if (item.tipoNormalizado === 'MAL') {
        wall.highItems.add(itemID)
      }
      if (item.tipoNormalizado === 'MBJ' || item.tipoNormalizado === 'COL') {
        wall.lowItems.add(itemID)
      }

      items.set(itemID, item)
      selectedItems.forEach((id) => {
        if (id === itemID) return // Saltar el elemento que se está rotando
        const selectedItem = items.get(id) as Item
        if (!selectedItem) return
        const itemDeltas = deltas.get(itemID)
        if (!itemDeltas) return
        const delta = itemDeltas.find(
          (d) =>
            d.deltaX === selectedItem.x - item.x &&
            d.deltaY === selectedItem.y - item.y &&
            d.deltaZ === selectedItem.z - item.z,
        )
        if (!delta) return
        const deltaVector = toVector3([delta.deltaX, delta.deltaY, delta.deltaZ])
        // Proyectar el delta en la dirección del projectedVector
        const deltaProjected = deltaVector.projectOnVector(projectedVector)
        // Calcular la nueva posición paralela al projectedVector
        const newPosition = adjustedPosition.clone().add(deltaProjected)

        selectedItem.matrix.setPosition(newPosition.x, newPosition.y, newPosition.z)
        selectedItem.x = newPosition.x
        selectedItem.y = newPosition.y
        selectedItem.z = newPosition.z

        items.set(id, selectedItem)
      })
    })
  },
  getFavorites: () => {
    const store = get()
    const selectedItems = store.layers[store.currentLayer].selected as Set<AnyID>
    const items = store.items
    const deltas = store.layers[store.currentLayer].deltas as Map<
      AnyID,
      { deltaX: number; deltaY: number; deltaZ: number }[]
    >

    const favorites = {
      items: new Map() as Map<AnyID, Item | LinkedItem>,
      deltas: new Map<AnyID, { deltaX: number; deltaY: number; deltaZ: number }[]>(),
    }

    selectedItems.forEach((id) => {
      const item = items.get(id) as Item | LinkedItem
      if (item) {
        favorites.items.set(id, item)
      }
    })

    if (selectedItems.size > 1) {
      favorites.deltas = deltas
    }

    return favorites
  },

  // Drag Selection Rectangle Actions
  setDragSelectionScreenStart: (point: [number, number] | null) => {
    set((store) => {
      store.dragSelectionScreenStart = point
    })
  },

  setDragSelectionScreenEnd: (point: [number, number] | null) => {
    set((store) => {
      store.dragSelectionScreenEnd = point
    })
  },

  setIsDragSelecting: (value: boolean) => {
    set((store) => {
      store.isDragSelecting = value
    })
  },

  selectObjectsInRectangle: (startWorld: [number, number], endWorld: [number, number]) => {
    set((store) => {
      // Crear un nuevo Set vacío para la selección
      const newSelectedItems = new Set<string>()

      // Calcular las coordenadas del rectángulo (min/max) en coordenadas del mundo
      const minX = Math.min(startWorld[0], endWorld[0])
      const maxX = Math.max(startWorld[0], endWorld[0])
      const minZ = Math.min(startWorld[1], endWorld[1])
      const maxZ = Math.max(startWorld[1], endWorld[1])

      console.log('🔍 Rectángulo de selección:', { minX, maxX, minZ, maxZ })

      // Recorrer todos los items y verificar si están dentro del rectángulo
      store.items.forEach((item, id) => {
        if (!item) return

        // Solo permitir seleccionar: itemGroup, items, linkedItem
        const isSelectableType =
          item.type === 'itemGroup' || item.type === 'items' || item.type === 'linkedItem'
        if (!isSelectableType) return

        // Excluir items invisibles
        if ('visible' in item && !item.visible) return

        // Obtener dimensiones y matriz
        if (!('matrix' in item) || !item.matrix) return
        if (!('width' in item) || !('thickness' in item)) return

        const itemWidth = item.width as number
        const itemThickness = item.thickness as number

        // Descomponer la matriz para obtener posición (esquina trasera inferior izquierda) y rotación
        const cornerPosition = toVector3()
        const quaternion = toQuaternion()
        const scale = toVector3()
        ;(item.matrix as Matrix4).decompose(cornerPosition, quaternion, scale)

        console.log(`📦 Item ${id}:`, {
          cornerPos: { x: cornerPosition.x, z: cornerPosition.z },
          dim: { width: itemWidth, thickness: itemThickness },
        })

        // Calcular las 4 esquinas del item en coordenadas locales
        // La posición de la matriz es la esquina trasera inferior izquierda (origen local)
        // Las esquinas se extienden desde ese origen en las direcciones +X (width) y +Z (thickness)
        const localCorners = [
          new Vector3(0, 0, 0), // Esquina trasera izquierda (origen)
          new Vector3(itemWidth, 0, 0), // Esquina trasera derecha
          new Vector3(0, 0, itemThickness), // Esquina frontal izquierda
          new Vector3(itemWidth, 0, itemThickness), // Esquina frontal derecha
        ]

        // Rotar cada esquina desde el origen y luego trasladar a la posición del item
        const worldCorners = localCorners.map((corner) => {
          const worldCorner = corner.clone()
          worldCorner.applyQuaternion(quaternion)
          worldCorner.add(cornerPosition)
          return {
            x: worldCorner.x,
            z: worldCorner.z,
          }
        })

        console.log('  Esquinas en mundo:', worldCorners)

        // Calcular cuántas esquinas están dentro del rectángulo
        const cornersInBounds = worldCorners.filter(
          (corner) => corner.x >= minX && corner.x <= maxX && corner.z >= minZ && corner.z <= maxZ,
        ).length

        console.log(`  ✓ Esquinas dentro: ${cornersInBounds}/4`)

        // Seleccionar si TODAS las 4 esquinas están dentro
        if (cornersInBounds === 4) {
          newSelectedItems.add(id)
        }
      })

      console.log('✅ Items seleccionados:', newSelectedItems.size)

      // Reemplazar el Set completo (dispara reactividad en Zustand)
      store.layers[store.currentLayer].selected = newSelectedItems

      // Actualizar currentObjectID con el último seleccionado si hay alguno
      if (newSelectedItems.size > 0) {
        const lastSelected = Array.from(newSelectedItems)[newSelectedItems.size - 1]
        store.currentObjectID = lastSelected as AnyID
      } else {
        store.currentObjectID = ''
      }
    })
  },

  selectObjectsInFrustum3D: (startNDC: Vector2, endNDC: Vector2, camera: Camera) => {
    set((store) => {
      // Crear un nuevo Set vacío para la selección
      const newSelectedItems = new Set<string>()

      // Calcular las coordenadas del rectángulo de selección en NDC (min/max)
      const minX = Math.min(startNDC.x, endNDC.x)
      const maxX = Math.max(startNDC.x, endNDC.x)
      const minY = Math.min(startNDC.y, endNDC.y)
      const maxY = Math.max(startNDC.y, endNDC.y)

      // Método de proyección: proyectar los vértices de cada OBB a pantalla
      // y verificar si alguno cae dentro del rectángulo de selección
      const tempVec = new Vector3()

      // Recorrer todos los items
      store.items.forEach((item, id) => {
        if (!item) return

        // Solo permitir seleccionar: itemGroup, items, linkedItem
        const isSelectableType =
          item.type === 'itemGroup' || item.type === 'items' || item.type === 'linkedItem'
        if (!isSelectableType) return

        // Excluir items invisibles
        if ('visible' in item && !item.visible) return

        // Obtener la OBB del item desde sceneBoundingBoxes
        const obbData = store.sceneBoundingBoxes.get(id)
        if (!obbData) return
        const obb = obbData.boundingBox

        // Obtener el centro y tamaños de la OBB
        const obbCenter = obb.center
        const halfSize = obb.halfSize
        const rotation = obb.rotation

        // Generar los 8 vértices de la OBB en espacio mundo
        const vertices: Vector3[] = []
        for (let i = 0; i < 8; i++) {
          const x = (i & 1) === 0 ? -halfSize.x : halfSize.x
          const y = (i & 2) === 0 ? -halfSize.y : halfSize.y
          const z = (i & 4) === 0 ? -halfSize.z : halfSize.z

          const vertex = new Vector3(x, y, z)
          vertex.applyMatrix3(rotation)
          vertex.add(obbCenter)
          vertices.push(vertex)
        }

        // Proyectar cada vértice a coordenadas de pantalla (NDC)
        // y verificar si TODAS las 8 esquinas están dentro del rectángulo de selección
        let allVerticesInside = true

        for (const vertex of vertices) {
          // Clonar para no modificar el original
          tempVec.copy(vertex)

          // Proyectar a NDC
          tempVec.project(camera)

          // Verificar si el vértice proyectado NO está dentro del rectángulo de selección
          // o está detrás de la cámara (z >= 1)
          if (
            tempVec.x < minX ||
            tempVec.x > maxX ||
            tempVec.y < minY ||
            tempVec.y > maxY ||
            tempVec.z >= 1 // Verificar que NO esté detrás de la cámara
          ) {
            allVerticesInside = false
            break
          }
        }

        // Seleccionar el item solo si TODAS las 8 esquinas están dentro del frustum
        if (allVerticesInside) {
          newSelectedItems.add(id)
        }
      })

      // Reemplazar el Set completo (dispara reactividad en Zustand)
      store.layers[store.currentLayer].selected = newSelectedItems

      // Actualizar currentObjectID con el último seleccionado si hay alguno
      if (newSelectedItems.size > 0) {
        const lastSelected = Array.from(newSelectedItems)[newSelectedItems.size - 1]
        store.currentObjectID = lastSelected as AnyID
      } else {
        store.currentObjectID = ''
      }
    })
  },
})
